import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import { fetchLabelMails } from '../../utils/labelApi';
import {
  parseSenderDisplay,
  getSenderInitial,
  getAvatarColor,
  getPreviewText,
  formatMailDate,
} from '../../utils/mailViewHelpers';
import MailReadingPane from '../MailReadingPane';
import { LabelIcon } from './LabelIcons';
import LabelBadge from './LabelBadge';
import './labels.css';
import '../Inbox.css';
import '../FolderView.css';

const getMailKey = (mail) => String(mail?._id || mail?.uid || mail?.messageId || '');
const isMailRead = (mail) => Boolean(mail?.seen ?? mail?.isRead ?? true);
const isImapMail = (mail) => mail && (mail.source === 'imap' || mail.uid != null);

const mergeOpenedMail = (base, detail = {}) => ({
  ...base,
  ...detail,
  labels: base.labels || detail.labels || [],
  body: detail.html || detail.body || detail.text || base.body || base.text || base.preview || '',
  detailsLoaded: true,
});

const LabelMails = () => {
  const { labelId } = useParams();
  let currentUserEmail = '';
  try {
    currentUserEmail = JSON.parse(localStorage.getItem('user') || '{}')?.email || '';
  } catch (_) { /* ignore */ }
  const [label, setLabel] = useState(null);
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const load = useCallback(async () => {
    if (!labelId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetchLabelMails(labelId);
      if (response.success) {
        setLabel(response.label || null);
        setMails(response.data || []);
      } else {
        setError(response.message || 'Failed to load labeled mail');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load labeled mail');
    } finally {
      setLoading(false);
    }
  }, [labelId]);

  useEffect(() => {
    setSelectedMail(null);
    setSelectedMailIds([]);
    setDetailError('');
    load();
  }, [load]);

  const handleSelectMail = (event, mailKey) => {
    event.stopPropagation();
    setSelectedMailIds((prev) =>
      prev.includes(mailKey) ? prev.filter((id) => id !== mailKey) : [...prev, mailKey]
    );
  };

  const handleToggleStar = async (event, mail) => {
    event.stopPropagation();
    const nextStarred = !mail.isStarred;
    const key = getMailKey(mail);
    setMails((prev) =>
      prev.map((item) => (getMailKey(item) === key ? { ...item, isStarred: nextStarred } : item))
    );
    if (selectedMail && getMailKey(selectedMail) === key) {
      setSelectedMail((prev) => (prev ? { ...prev, isStarred: nextStarred } : prev));
    }
    try {
      if (isImapMail(mail) && mail.uid != null) {
        await axiosInstance.patch(`/api/mail/server/star/${mail.uid}`, { starred: nextStarred });
      } else if (mail._id) {
        await axiosInstance.patch(`/api/mail/star/${mail._id}`, { isStarred: nextStarred });
      }
    } catch (err) {
      console.error('Error toggling star:', err);
      setMails((prev) =>
        prev.map((item) => (getMailKey(item) === key ? { ...item, isStarred: !nextStarred } : item))
      );
    }
  };

  const openMail = async (mail) => {
    setDetailError('');
    setDetailLoading(true);
    setSelectedMail(
      mergeOpenedMail(mail, {
        body: mail.body || mail.html || mail.text || mail.preview || '',
      })
    );
    try {
      if (isImapMail(mail) && mail.uid != null && !mail.detailsLoaded) {
        const response = await axiosInstance.get(`/api/mail/server/message/${mail.uid}`);
        if (response.data.success) {
          setSelectedMail(mergeOpenedMail(mail, response.data.data));
        }
        return;
      }
      if (mail._id && !mail.detailsLoaded && !(mail.body || mail.html || mail.text)) {
        const response = await axiosInstance.get(`/api/mail/${mail._id}`);
        if (response.data.success) {
          setSelectedMail(mergeOpenedMail(mail, response.data.data));
        }
      }
    } catch (err) {
      console.error('Error opening labeled mail:', err);
      setDetailError(err.response?.data?.message || 'Could not open message');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedMail(null);
    setDetailError('');
  };

  const totalCount = mails.length;

  return (
    <div
      className={`inbox-container premium-mail-dashboard reading-pane-no-split labels-inbox-shell${
        selectedMail ? ' mail-detail-open' : ''
      }`}
    >
      <div
        className={`inbox-content reading-pane-no-split gmail-split-layout${
          selectedMail ? ' has-detail-open full-detail-view' : ''
        }`}
      >
        <div className={`mail-list-container${selectedMail ? ' hidden' : ' full-width'}`}>
          <div className="labels-inbox-header">
            <h2 style={{ color: label?.color || '#202124' }}>
              <LabelIcon name={label?.icon || 'label'} size={20} color={label?.color || '#1a73e8'} />
              {label?.name || 'Label'}
            </h2>
            {totalCount > 0 && (
              <div className="mail-list-meta">
                <span className="mail-list-meta-text">
                  1–{totalCount} of {totalCount}
                </span>
              </div>
            )}
          </div>

          <div className="mail-list">
            {loading && <div className="labels-loading">Loading labeled mail...</div>}
            {!loading && error && <div className="labels-error">{error}</div>}
            {!loading && !error && mails.length === 0 && (
              <div className="empty-inbox">
                <p>No conversations with this label</p>
                <p className="empty-hint">Apply this label to messages from Inbox.</p>
              </div>
            )}

            {!loading &&
              !error &&
              mails.map((mail) => {
                const mailKey = getMailKey(mail);
                const preview = getPreviewText(mail);
                const avatarColors = getAvatarColor(mail.from);
                const isSelected = selectedMailIds.includes(mailKey);
                const isActive = selectedMail && getMailKey(selectedMail) === mailKey;

                return (
                  <div
                    key={mailKey}
                    role="button"
                    tabIndex={0}
                    className={`mail-item gmail-split-row ${!isMailRead(mail) ? 'unread' : 'read'}${
                      isActive ? ' selected' : ''
                    }${isSelected ? ' mail-selected' : ''}`}
                    onClick={() => openMail(mail)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openMail(mail);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      className="mail-checkbox gmail-col-check"
                      checked={isSelected}
                      onChange={(event) => handleSelectMail(event, mailKey)}
                      onClick={(event) => event.stopPropagation()}
                      aria-label="Select mail"
                    />
                    <button
                      type="button"
                      className={`mail-star-btn gmail-row-star gmail-row-star-left ${
                        mail.isStarred ? 'starred' : ''
                      }`}
                      onClick={(event) => handleToggleStar(event, mail)}
                      title={mail.isStarred ? 'Unstar' : 'Star'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        {mail.isStarred ? (
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        ) : (
                          <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
                        )}
                      </svg>
                    </button>
                    <div
                      className="mail-sender-avatar"
                      style={{ backgroundColor: avatarColors.bg, color: avatarColors.color }}
                      aria-hidden="true"
                    >
                      {getSenderInitial(mail.from)}
                    </div>
                    <div className="gmail-row-body">
                      <div className="gmail-row-primary">
                        <span className="mail-sender">{parseSenderDisplay(mail.from, currentUserEmail)}</span>
                        <div className="gmail-row-main-line">
                          <div className="gmail-row-subject">
                            {(mail.labels || []).slice(0, 2).map((entry) => (
                              <LabelBadge key={entry._id} label={entry} />
                            ))}
                            <span className="gmail-row-subject-text">
                              {mail.subject || '(no subject)'}
                            </span>
                          </div>
                          {preview ? <span className="gmail-row-snippet">{preview}</span> : null}
                        </div>
                      </div>
                    </div>
                    <div className="gmail-row-aside">
                      <span className="mail-date">{formatMailDate(mail.createdAt || mail.date)}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {selectedMail && (
          <MailReadingPane
            mail={selectedMail}
            folderLabel={label?.name || 'Label'}
            onClose={closeDetail}
            headerExtras={(selectedMail.labels || []).map((entry) => (
              <LabelBadge key={entry._id} label={entry} />
            ))}
          >
            {detailLoading && <div className="labels-loading">Opening message...</div>}
            {!detailLoading && detailError && <div className="labels-error">{detailError}</div>}
          </MailReadingPane>
        )}
      </div>
    </div>
  );
};

export default LabelMails;
